import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import SideNav from '../../common/SideNav';
import {
    hasSideBar,
    toggleSideBar,
    updateCurrentComponent,
} from '../../../store/actions/navActions';
import { faqList } from './FaqList';
import * as C from '../../../services/constants';

import chevronDownIcon from '../../../resources/images/chevron-down.svg';
import chevronUpIcon from '../../../resources/images/chevron-up.svg';

const NewHelp = () => {
    const dispatch = useDispatch();

    const [expandedItems, setExpandedItems] = useState([]);
    const [allExpanded, setAllExpanded] = useState(false);

    useEffect(() => {
        dispatch(updateCurrentComponent(C.APP_HELP_PAGE_TITLE));
        dispatch(hasSideBar(true));
        dispatch(toggleSideBar(false));
    });

    const handleToggleAccordion = (index) => {
        let newExpandedItems = [...expandedItems];
        const currentIndex = newExpandedItems.indexOf(index);

        if (currentIndex === -1) {
            newExpandedItems.push(index);
        } else {
            newExpandedItems.splice(currentIndex, 1);
        }

        setExpandedItems(newExpandedItems);
        setAllExpanded(newExpandedItems.length === faqList.length);
    };

    const handleCollapseAll = () => {
        setExpandedItems([]);
        setAllExpanded(false);
    };

    const handleExpandAll = () => {
        const allIndexes = faqList.map((_, index) => index);
        setExpandedItems(allIndexes);
        setAllExpanded(true);
    };

    return (
        <div className="component-container-root">
            <SideNav />
            <div className="help-container">
                <h2>How to Manage the ZCDP Program</h2>
                <br/>
                <h3>Add Rule</h3>
                <ul>
                    <li>
                        The Add Rule page allows you to create and schedule
                        rules that define automated data triggers and processing
                        logic in the Zero Copy CDP platform.
                    </li>
                </ul>
                <h3>Rule Group Section</h3>
                <ul>
                    <li>
                        Each Rule Group represents a set of conditions that
                        determine when the rule should trigger.
                    </li>
                    <li>
                        <strong>Entity:</strong> Select the data entity (e.g.,
                        Device, Consumer, etc.) on which the rule will apply.
                    </li>
                    <li>
                        <strong>Since this is customer segmentation:</strong> We
                        need to use the customer attribute as mandatorily.
                    </li>
                    <li>
                        <strong>Event Attribute:</strong> Choose the attribute
                        to evaluate (e.g., Device Type Code).
                    </li>
                    <li>
                        <strong>Operation:</strong> Define the comparison logic
                        (e.g., Equals, Contains, Greater Than).
                    </li>
                    <li>
                        <strong>Attribute Value:</strong> Enter the value to
                        compare against (e.g., D0101).
                    </li>
                    <li>
                        <strong>Add (+) Button:</strong> Add multiple conditions
                        within the same rule group.
                    </li>
                    <li>
                        <strong>Aggregator:</strong> Use this to apply
                        aggregation logic (e.g., Order By).
                    </li>
                    <li>
                        <strong>Add Rule / Add Rule Group:</strong>
                        <ul>
                            <li>
                                Add Rule adds another condition within the same
                                group.
                            </li>
                            <li>
                                Add Rule Group allows combining multiple groups
                                with logical connectors (e.g., AND / OR).
                            </li>
                        </ul>
                    </li>
                </ul>

                <h3>Schedule Section</h3>
                <p>Set when and how often the rule runs.</p>
                <ul>
                    <li>
                        <strong>Frequency:</strong> Select how often the rule
                        executes (e.g., Daily, Weekly, Monthly).
                    </li>
                    <li>
                        <strong>First Execution Date (UTC):</strong> The date
                        the rule will first run.
                    </li>
                    <li>
                        <strong>Schedule Run Time (UTC):</strong> The time of
                        day (in UTC) when the rule should execute.
                    </li>
                    <li>
                        <strong>Note:</strong> If the first execution date is
                        today, only future times can be selected. Run dates can
                        be only scheduled in the future date.
                    </li>
                    <li>
                        <strong>Load Type:</strong>
                        <ul>
                            <li>
                                <strong>Full:</strong> Runs the rule on the
                                complete dataset.
                            </li>
                            <li>
                                <strong>Incremental:</strong> Runs only on newly
                                added or updated data (if supported).
                            </li>
                        </ul>
                    </li>
                </ul>

                <h3>Rule Name</h3>
                <ul>
                    <li>
                        Enter a meaningful name for your rule (e.g., PH IQOS
                        TEST). This helps identify it later in the rule list.
                    </li>
                </ul>

                <h3>Actions</h3>
                <ul>
                    <li>
                        <strong>Publish Rule:</strong> Saves and activates the
                        rule for execution based on the defined schedule.
                    </li>
                    <li>
                        <strong>Save as Draft:</strong> Saves the rule
                        configuration without activating it.
                    </li>
                    <li>
                        <strong>Delete:</strong> Removes the current rule
                        configuration (Only applicable for Draft status rule).
                    </li>
                </ul>

                <div className="faq-header">
                    <h2>Frequently Asked Questions</h2>
                    <button
                        className="faq-collapse-all"
                        onClick={
                            allExpanded ? handleCollapseAll : handleExpandAll
                        }
                    >
                        <img
                            src={allExpanded ? chevronUpIcon : chevronDownIcon}
                            alt="collapse all"
                        />{' '}
                        {allExpanded ? 'Collapse All' : 'Expand All'}
                    </button>
                </div>
                <div style={{ width: '70%' }}>
                    {faqList.map((faq, index) => (
                        <Accordion
                            key={index}
                            expanded={expandedItems.includes(index)}
                            onChange={() => handleToggleAccordion(index)}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <h3>{faq.question}</h3>
                            </AccordionSummary>
                            <AccordionDetails>
                                <ul>
                                    {faq.answer.map((ans, idx) => (
                                        <li
                                            key={idx}
                                            dangerouslySetInnerHTML={{
                                                __html: ans,
                                            }}
                                        />
                                    ))}
                                </ul>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default NewHelp;
